import type {
  FreshStrawDiscardRequest,
  FreshStrawFreezeRequest,
  FreshStrawImplantRequest,
  FreshStrawStageGradeEntry,
} from '@/app/services/freshStrawRest';

import type { FreshStrawQCForm } from '../../types';

/**
 * Pure payload builders + validators for the three fresh-straw save actions.
 *
 * Each save operates on one fertilization row at a time (the legacy MVC model —
 * see `tog-freshstrawtransfer/src/Repositories/ListRepository.cs:1582`), so a
 * caller that has multiple selected rows iterates and calls the builder per row.
 *
 * Builders do NOT throw and do NOT validate. Validation lives in `validate*`
 * functions that return a list of error messages. The caller is expected to
 *   1. call `validate*(row, shared)` and bail out if errors are non-empty, then
 *   2. call `build*Payload(row, shared)` to get a well-typed request body.
 */

/**
 * One selected fertilization row contributing a save. The stage/grade entries
 * come from the per-row stage/grade dialog; other fields come from the loaded
 * fertilization record.
 */
export interface FreshStrawRow {
  fertilizationId: string;
  /** Stage/grade matrix the user entered in the per-row dialog. */
  entries: FreshStrawStageGradeEntry[];
  /** Optional per-row staging id — passed through as @embryostagingid. */
  embryoStagingId?: string | null;
  /** Optional per-row fresh-straw number override. */
  freshStrawNum?: string | null;
}

/**
 * Fields that apply to every row in a batch save. These come from the action
 * panel form + URL session state + the Redux QC slice.
 */
export interface FreshStrawSharedInputs {
  transferDate: string | undefined;
  transferSite: string | undefined;
  grader?: string | null;
  graderId?: string | null;
  loadById?: string | null;
  loadDate?: string | null;
  qc?: Partial<FreshStrawQCForm> | null;
}

export interface FreshStrawImplantSharedInputs extends FreshStrawSharedInputs {
  recip: string;
  farmLocationId?: string | null;
  autoAssignStraws: boolean;
  calfType?: number | null;
}

export interface FreshStrawFreezeSharedInputs extends FreshStrawSharedInputs {
  freezeDate: string | undefined;
  freezerId?: string | null;
}

export type FreshStrawDiscardSharedInputs = FreshStrawSharedInputs;

// ───────────────────────── validators ─────────────────────────

function requireNonEmpty(
  value: string | undefined | null,
  label: string,
): string | null {
  return value ? null : `${label} is required`;
}

function requireEntries(entries: FreshStrawStageGradeEntry[]): string | null {
  if (!entries || entries.length === 0)
    return 'Stage/grade entries are required';
  const total = entries.reduce((sum, e) => sum + (e?.count ?? 0), 0);
  if (total <= 0) return 'Stage/grade entries must total at least 1';
  return null;
}

function commonSharedErrors(
  row: FreshStrawRow,
  shared: FreshStrawSharedInputs,
): string[] {
  const errors: string[] = [];
  const pushIf = (err: string | null) => {
    if (err) errors.push(err);
  };

  pushIf(requireNonEmpty(row.fertilizationId, 'Fertilization'));
  pushIf(requireEntries(row.entries));
  pushIf(requireNonEmpty(shared.transferSite, 'Transfer site'));
  return errors;
}

export function validateImplantInputs(
  row: FreshStrawRow,
  shared: FreshStrawImplantSharedInputs,
): string[] {
  const errors = commonSharedErrors(row, shared);
  if (!shared.transferDate) errors.push('Transfer date is required');
  if (!shared.recip) errors.push('Recip is required for fresh implant');
  return errors;
}

export function validateFreezeInputs(
  row: FreshStrawRow,
  shared: FreshStrawFreezeSharedInputs,
): string[] {
  const errors = commonSharedErrors(row, shared);
  if (!shared.freezeDate) errors.push('Freeze date is required');
  return errors;
}

export function validateDiscardInputs(
  row: FreshStrawRow,
  shared: FreshStrawDiscardSharedInputs,
): string[] {
  return commonSharedErrors(row, shared);
}

// ───────────────────────── builders ─────────────────────────

function pickQcFields(qc: Partial<FreshStrawQCForm> | null | undefined) {
  return {
    ehmDate: qc?.ehmDate
      ? qc.ehmDate instanceof Date
        ? qc.ehmDate.toISOString()
        : String(qc.ehmDate)
      : null,
    strawLotNo: qc?.strawLotNumber ?? null,
    strawIncubatorNo: qc?.strawIncubatorNumber ?? null,
    gunWarmerTemp: qc?.gunWarmerTemp ?? null,
  };
}

function sharedHeader(
  row: FreshStrawRow,
  shared: FreshStrawSharedInputs,
  siteId: string,
) {
  return {
    fertId: row.fertilizationId,
    siteId,
    grader: shared.grader ?? null,
    graderId: shared.graderId ?? null,
    loadById: shared.loadById ?? null,
    loadDate: shared.loadDate ?? null,
    embryoStagingId: row.embryoStagingId ?? null,
    entries: row.entries,
    ...pickQcFields(shared.qc),
  };
}

export function buildImplantPayload(
  row: FreshStrawRow,
  shared: FreshStrawImplantSharedInputs,
): FreshStrawImplantRequest {
  return {
    ...sharedHeader(row, shared, shared.transferSite ?? ''),
    transferDate: shared.transferDate ?? '',
    farmLocationId: shared.farmLocationId ?? null,
    autoAssignStraws: shared.autoAssignStraws,
    freshStrawNum: row.freshStrawNum ?? null,
    calfType: shared.calfType ?? null,
    recip: shared.recip,
  };
}

export function buildFreezePayload(
  row: FreshStrawRow,
  shared: FreshStrawFreezeSharedInputs,
): FreshStrawFreezeRequest {
  return {
    ...sharedHeader(row, shared, shared.transferSite ?? ''),
    freezeDate: shared.freezeDate ?? '',
    freezerId: shared.freezerId ?? null,
    freshStrawNum: row.freshStrawNum ?? null,
  };
}

export function buildDiscardPayload(
  row: FreshStrawRow,
  shared: FreshStrawDiscardSharedInputs,
): FreshStrawDiscardRequest {
  return {
    ...sharedHeader(row, shared, shared.transferSite ?? ''),
    freshStrawNum: row.freshStrawNum ?? null,
  };
}
